import React from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';

// Tracks the steps an import goes through (checking the studio, adding vendors,
// copying photos, saving) for ImportProgress. Each step's total is set when the
// step is planned, so the bar only ever moves forward.
export function createProgress(onChange, title, steps) {
  const current = steps.map((step) => ({ total: 0, done: 0, state: 'waiting', ...step }));
  const find = (key) => current.find((step) => step.key === key);
  const emit = () => onChange({ title, steps: current.map((step) => ({ ...step })) });
  emit();
  return {
    plan(key, total) {
      find(key).total = total;
      emit();
    },
    start(key) {
      find(key).state = 'active';
      emit();
    },
    tick(key, count = 1) {
      const step = find(key);
      step.done = Math.min(step.total, step.done + count);
      emit();
    },
    finish(key) {
      const step = find(key);
      Object.assign(step, { state: 'done', done: step.total });
      emit();
    },
  };
}

// The checking step counts as one unit of work, so the bar starts moving
// before anything is saved.
export function progressPercent(steps) {
  const units = (step) => (step.key === 'check' ? 1 : step.total);
  const done = (step) => (step.key === 'check' ? (step.state === 'done' ? 1 : 0) : step.done);
  const total = steps.reduce((sum, step) => sum + units(step), 0);
  if (!total) return 0;
  return Math.floor((steps.reduce((sum, step) => sum + done(step), 0) / total) * 100);
}

function stepCount(step) {
  if (step.key === 'check') return '';
  if (step.state === 'done') return step.total ? `${step.total} of ${step.total}` : 'None needed';
  return step.total ? `${step.done} of ${step.total}` : '';
}

// Shown over the page while an import runs, so it's clear what's happening and
// how far along it is, and nothing can be edited mid-save.
export default function ImportProgress({ progress }) {
  if (!progress) return null;
  const percent = progressPercent(progress.steps);
  return createPortal(
    <div className="pi-page">
      <div className="pi-modal-overlay">
        <div
          className="pi-modal pi-progress"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pi-progress-title"
        >
          <h3 id="pi-progress-title">{progress.title}</h3>
          <div
            className="pi-progress-bar"
            role="progressbar"
            aria-label="Import progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <span style={{ width: `${percent}%` }} />
          </div>
          <p className="pi-progress-percent">{percent}% done</p>
          <ol className="pi-progress-steps">
            {progress.steps.map((step) => (
              <li key={step.key} className={`is-${step.state}`}>
                {step.state === 'done' ? (
                  <CheckCircle2 size={18} aria-hidden="true" />
                ) : step.state === 'active' ? (
                  <Loader2 size={18} className="pi-spin" aria-hidden="true" />
                ) : (
                  <Circle size={18} aria-hidden="true" />
                )}
                <span className="pi-progress-label">{step.label}</span>
                <span className="pi-progress-count">{stepCount(step)}</span>
              </li>
            ))}
          </ol>
          <p className="pi-progress-note">Keep this tab open until the import finishes.</p>
        </div>
      </div>
    </div>,
    document.body
  );
}
